import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ReactNode, SelectHTMLAttributes } from 'react'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import '../Input/Input.css'
import './Select.css'

export interface SelectOption {
  id: string
  label: string
  /** Código visual breve antes del texto, p. ej. "ES" en idioma. */
  badge?: string
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'onChange' | 'value'> {
  label: ReactNode
  labelNote?: ReactNode
  help?: ReactNode
  error?: ReactNode
  /** "Campo desactivado con motivo" (bundle Acompañante reg3) */
  disabledReason?: ReactNode
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  /** Texto de la opción vacía; sin él no hay opción vacía */
  placeholder?: string
  /** Bandera o icono delante del valor */
  leading?: ReactNode
  /** Sustituye el menú nativo por un menú con filtro. Úsalo en listas largas, como países. */
  searchable?: boolean
  /** Menú personalizado sin filtro, para listas cortas como el selector de idioma. */
  customMenu?: boolean
  searchLabel?: string
  searchPlaceholder?: string
  noResultsText?: string
}

/**
 * Selector con la anatomía de Input. Las listas cortas conservan el <select> nativo; las largas pueden
 * activar un menú propio con búsqueda sin cambiar la API de los campos existentes.
 */
export function Select({
  label, labelNote, help, error, disabledReason, options, value, onChange, placeholder, leading, searchable = false, customMenu = false,
  searchLabel = 'Buscar', searchPlaceholder = 'Escribe para buscar', noResultsText = 'No hay resultados', id, className, disabled, ...rest
}: SelectProps) {
  const uid = useId()
  const selectId = id ?? `hp-select-${uid}`
  const noteId = `${selectId}-note`
  const labelId = `${selectId}-label`
  const menuId = `${selectId}-menu`
  const isDisabled = disabled || Boolean(disabledReason)
  const usesCustomMenu = searchable || customMenu
  const note = error ?? disabledReason ?? help
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const selected = options.find((option) => option.id === value)
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase()
    return normalizedQuery ? options.filter((option) => `${option.badge ?? ''} ${option.label}`.toLocaleLowerCase().includes(normalizedQuery)) : options
  }, [options, query])

  useEffect(() => {
    if (!isOpen) return
    searchRef.current?.focus()
    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsidePointer)
    return () => document.removeEventListener('mousedown', closeOnOutsidePointer)
  }, [isOpen])

  const openMenu = () => {
    if (isDisabled) return
    setQuery('')
    setIsOpen(true)
  }

  const selectOption = (optionId: string) => {
    onChange(optionId)
    setIsOpen(false)
  }

  return (
    <div ref={rootRef} className={cx('hp-field', 'hp-select', usesCustomMenu && 'hp-select--searchable', error && 'hp-field--error', isDisabled && 'hp-field--disabled', className)}>
      <label id={labelId} htmlFor={usesCustomMenu ? undefined : selectId} className="hp-field__label">
        {label}
        {labelNote && <span className="hp-field__label-note"> {labelNote}</span>}
      </label>
      <div className="hp-field__control">
        {leading && <span className="hp-field__leading">{leading}</span>}
        {usesCustomMenu ? (
          <>
            <button
              id={selectId}
              type="button"
              className="hp-field__input hp-select__trigger"
              onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
              aria-labelledby={labelId}
              aria-describedby={note ? noteId : undefined}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
              aria-controls={isOpen ? menuId : undefined}
              disabled={isDisabled}
            >
              {selected ? <OptionLabel option={selected} /> : placeholder ?? ''}
            </button>
            {isOpen && (
              <div id={menuId} className="hp-select__menu" role="dialog" aria-label={searchable ? searchLabel : typeof label === 'string' ? label : undefined}>
                {searchable && (
                  <div className="hp-select__search">
                    <Icon name="search" size={18} className="hp-select__search-icon" />
                    <input
                      ref={searchRef}
                      type="search"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setIsOpen(false)
                      }}
                      placeholder={searchPlaceholder}
                      aria-label={searchLabel}
                    />
                  </div>
                )}
                <ul className="hp-select__options" aria-labelledby={labelId}>
                  {filteredOptions.length ? filteredOptions.map((option) => (
                    <li key={option.id}>
                      <button type="button" className={cx('hp-select__option', option.id === value && 'hp-select__option--selected')} onClick={() => selectOption(option.id)}>
                        <OptionLabel option={option} />
                        {option.id === value && <Icon name="check" size={18} />}
                      </button>
                    </li>
                  )) : <li className="hp-select__empty">{noResultsText}</li>}
                </ul>
              </div>
            )}
          </>
        ) : (
          <select
            id={selectId}
            className="hp-field__input hp-select__input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={note ? noteId : undefined}
            disabled={isDisabled}
            {...rest}
          >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.badge ? `${o.badge} ${o.label}` : o.label}
              </option>
            ))}
          </select>
        )}
        <Icon name="chevron-down" size={20} className="hp-select__chevron" />
      </div>
      <div id={noteId} className="hp-field__note" aria-live={error ? 'polite' : undefined}>
        {error ? (
          <>
            <Icon name="alert-triangle" size={18} className="hp-field__note-icon" />
            <span>{error}</span>
          </>
        ) : (
          note
        )}
      </div>
    </div>
  )
}

function OptionLabel({ option }: { option: SelectOption }) {
  return (
    <span className="hp-select__choice">
      {option.badge && <span className="hp-select__badge">{option.badge}</span>}
      <span>{option.label}</span>
    </span>
  )
}
